import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RichDiffViewer from "../../components/RichDiffViewer";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "code", label: "Code" },
  { value: "documentation", label: "Docs" },
  { value: "sql", label: "SQL" },
  { value: "missing_diff", label: "No Diff" },
];

const HUMAN_REVIEW_STATUS_OPTIONS = [
  { value: "not_started", label: "Not started" },
  { value: "needs_review", label: "Needs review" },
  { value: "approved", label: "Approved" },
  { value: "changes_requested", label: "Changes requested" },
  { value: "rejected", label: "Rejected" },
  { value: "overridden", label: "Overridden" },
];

const getStateChip = (state) => {
  const value = String(state || "unknown").toLowerCase();
  if (value === "new") return { label: "New", color: "success" };
  if (value === "deleted") return { label: "Deleted", color: "error" };
  if (value === "changed") return { label: "Changed", color: "primary" };
  return { label: "Unknown", color: "default" };
};

const getRowKey = (row, index) =>
  `${row.change_id || "change"}:${row.id || row.path || row.file_path || index}`;

const getRowPath = (row) => row.path || row.file_path || "(untracked path)";

const getRowKind = (row) => {
  const assetType = String(row.asset_type || "").toLowerCase();
  if (assetType === "documentation") return "documentation";
  if (assetType === "sql") return "sql";
  const kind = String(row.kind || "").toLowerCase();
  if (kind === "documentation") return "documentation";
  if (kind === "sql") return "sql";
  return "code";
};

const formatStatusLabel = (value) => String(value || "")
  .replace(/_/g, " ")
  .replace(/\b\w/g, (char) => char.toUpperCase());

const buildSqlSummaryText = (sqlSummary) => {
  if (!sqlSummary || !sqlSummary.total_count) return "No SQL artifacts linked to this Full Change.";
  const unresolved = sqlSummary.unresolved_count || 0;
  if (unresolved) return `${unresolved} unresolved SQL artifact${unresolved === 1 ? "" : "s"} must be resolved before closeout.`;
  return "SQL artifact review/execution evidence is complete.";
};

const getDiffStatusChip = (row) => {
  const status = row.diff_status || (row.diff_available || row.preview_log_id || row.diff_text ? "available" : "history_only");
  if (status === "available") return { label: "Diff Available", color: "success", variant: "filled" };
  if (status === "missing") return { label: "Missing Diff", color: "warning", variant: "filled" };
  return { label: "History Only", color: "default", variant: "outlined" };
};

const getVerificationChip = (verificationStatus) => {
  const state = String(verificationStatus?.state || "not_run").toLowerCase();
  if (state === "passed") return { label: "Verification: Passed", color: "success", variant: "filled" };
  if (state === "failed") return { label: "Verification: Failed", color: "error", variant: "filled" };
  if (state === "running") return { label: "Verification: Running", color: "info", variant: "filled" };
  if (state === "pending") return { label: "Verification: Pending", color: "warning", variant: "outlined" };
  return { label: "Verification: Not run", color: "default", variant: "outlined" };
};

const getStepChipColor = (state) => {
  const value = String(state || "").toLowerCase();
  if (value === "success") return "success";
  if (value === "failure") return "error";
  if (value === "running") return "info";
  if (value === "pending") return "warning";
  return "default";
};

const FullChangeReviewWorkspace = ({ fullChangeId, selectedFullChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [context, setContext] = useState(null);
  const [filter, setFilter] = useState("all");
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState("");
  const [diffTitle, setDiffTitle] = useState("");
  const [diffText, setDiffText] = useState("");
  const [humanReviewDrafts, setHumanReviewDrafts] = useState({});
  const [humanReviewSavingId, setHumanReviewSavingId] = useState(null);
  const [humanReviewError, setHumanReviewError] = useState("");

  const loadReviewContext = async () => {
    if (!fullChangeId) {
      setContext(null);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        full_change_id: String(fullChangeId),
        include_diff: "true",
        diff_char_limit: "8000",
      });
      const res = await fetch(`/api/dev-ide/full-change/review-context?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Review workspace failed to load.");
      }
      setContext(data);
      setHumanReviewError("");
      window.dispatchEvent(new CustomEvent("devide:full-change-loc-refresh", {
        detail: { fullChangeId, source: "review_workspace_refresh" },
      }));
    } catch (exc) {
      setContext(null);
      setError(exc.message || "Review workspace failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviewContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullChangeId]);

  useEffect(() => {
    const nextDrafts = {};
    (context?.linked_changes || []).forEach((change) => {
      const changeId = change.change_id || change.id;
      if (!changeId) return;
      nextDrafts[changeId] = {
        status: change.human_review?.status || "not_started",
        notes: change.human_review?.notes || "",
      };
    });
    setHumanReviewDrafts(nextDrafts);
  }, [context]);

  const summary = context?.summary || {};
  const sqlArtifactSummary = context?.sql_artifact_summary || {};
  const groupedChanges = useMemo(() => context?.linked_changes || [], [context]);
  const visibleGroups = useMemo(() => {
    return groupedChanges
      .map((change) => {
        const rows = (change.files || []).filter((row) => {
          if (filter === "all") return true;
          if (filter === "missing_diff") return !row.diff_available && !row.preview_log_id && !row.diff_text;
          return getRowKind(row) === filter;
        });
        return { ...change, reviewRows: rows };
      })
      .filter((change) => change.reviewRows.length > 0 || filter === "all");
  }, [filter, groupedChanges]);

  const openDiff = async (row) => {
    setDiffDialogOpen(true);
    setDiffTitle(getRowPath(row));
    setDiffError("");
    setDiffText(row.diff_text || "");
    setDiffLoading(false);

    if (row.diff_text || !row.preview_log_id) {
      return;
    }

    setDiffLoading(true);
    try {
      const res = await fetch(`/api/dev-ide/preview-diff-log?log_id=${encodeURIComponent(row.preview_log_id)}`);
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.error || "Diff log failed to load.");
      }
      setDiffText(data.log?.diff_text || "(no diff text stored)");
    } catch (exc) {
      setDiffError(exc.message || "Diff log failed to load.");
    } finally {
      setDiffLoading(false);
    }
  };

  const updateHumanReviewDraft = (changeId, patch) => {
    setHumanReviewDrafts((prev) => ({
      ...prev,
      [changeId]: {
        status: prev[changeId]?.status || "not_started",
        notes: prev[changeId]?.notes || "",
        ...patch,
      },
    }));
  };

  const saveHumanReview = async (change) => {
    const changeId = change.change_id || change.id;
    if (!fullChangeId || !changeId) return;

    const draft = humanReviewDrafts[changeId] || {
      status: change.human_review?.status || "not_started",
      notes: change.human_review?.notes || "",
    };

    setHumanReviewSavingId(changeId);
    setHumanReviewError("");
    try {
      const res = await fetch("/api/dev-ide/full-change/review-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_change_id: fullChangeId,
          dev_ide_change_id: changeId,
          status: draft.status || "not_started",
          notes: draft.notes || "",
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error((data.errors && data.errors.join(", ")) || data.error || "Human review status failed to save.");
      }
      setContext((prev) => ({
        ...(prev || {}),
        linked_changes: (prev?.linked_changes || []).map((item) => {
          const itemId = item.change_id || item.id;
          if (String(itemId) !== String(changeId)) return item;
          return { ...item, human_review: data.human_review || item.human_review };
        }),
      }));
    } catch (exc) {
      setHumanReviewError(exc.message || "Human review status failed to save.");
    } finally {
      setHumanReviewSavingId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "secondary.light", backgroundColor: "rgba(156,39,176,0.03)" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, mb: 1 }}>
        <Box>
          <Typography variant="h6">Full Change Review Workspace</Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedFullChange?.title || "Selected Full Change"} - linked code and documentation outputs.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: { xs: "stretch", sm: "center" } }}>
          <TextField
            size="small"
            select
            label="Rows"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            sx={{ minWidth: 140 }}
          >
            {FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" onClick={loadReviewContext} disabled={loading || !fullChangeId}>
            {loading ? "Refreshing..." : "Refresh Review"}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mb: 1 }}>
        <Chip size="small" label={`Linked: ${summary.linked_change_count ?? 0}`} variant="outlined" />
        <Chip size="small" label={`Code: ${summary.code_file_count ?? 0}`} variant="outlined" />
        <Chip size="small" label={`Docs: ${summary.documentation_row_count ?? 0}`} variant="outlined" />
        <Chip size="small" label={`Diffs: ${summary.diff_available_count ?? 0}`} variant="outlined" />
        <Chip size="small" label={`Missing: ${summary.diff_missing_count ?? 0}`} color="warning" variant="outlined" />
        <Chip size="small" label={`History only: ${summary.history_only_count ?? 0}`} variant="outlined" />
        <Chip
          size="small"
          label={`SQL: ${summary.sql_artifact_count ?? sqlArtifactSummary.total_count ?? 0}`}
          color={sqlArtifactSummary.has_unresolved_blockers ? "error" : "default"}
          variant={sqlArtifactSummary.total_count ? "outlined" : "outlined"}
        />
        {sqlArtifactSummary.total_count ? (
          <Chip
            size="small"
            label={`SQL unresolved: ${sqlArtifactSummary.unresolved_count ?? 0}`}
            color={sqlArtifactSummary.has_unresolved_blockers ? "error" : "success"}
            variant={sqlArtifactSummary.has_unresolved_blockers ? "filled" : "outlined"}
          />
        ) : null}
        {summary.diff_truncated_count ? (
          <Chip size="small" label={`Truncated: ${summary.diff_truncated_count}`} color="warning" variant="outlined" />
        ) : null}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : null}

      {error ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      {humanReviewError ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {humanReviewError}
        </Alert>
      ) : null}

      {context && sqlArtifactSummary.total_count ? (
        <Alert severity={sqlArtifactSummary.has_unresolved_blockers ? "error" : "success"} sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            SQL closeout gate: {sqlArtifactSummary.closeout_status === "blocked" ? "Blocked" : "Clear"}
          </Typography>
          <Typography variant="body2">
            {sqlArtifactSummary.closeout_message || buildSqlSummaryText(sqlArtifactSummary)}
          </Typography>
          {sqlArtifactSummary.has_unresolved_blockers ? (
            <Box component="ul" sx={{ mt: 0.75, mb: 0, pl: 2 }}>
              {(sqlArtifactSummary.blockers || []).slice(0, 5).map((blocker) => (
                <li key={`${blocker.artifact_id || blocker.sql_file_path}:${blocker.blocker_type}`}>
                  <Typography variant="caption">
                    {blocker.message || `${blocker.sql_file_path || blocker.sql_name || "SQL artifact"}: ${formatStatusLabel(blocker.status)}`}
                  </Typography>
                </li>
              ))}
            </Box>
          ) : null}
        </Alert>
      ) : null}

      {!loading && !error && !context ? (
        <Typography variant="body2" color="text.secondary">
          Select a Full Change to load review rows.
        </Typography>
      ) : null}

      {!loading && context && visibleGroups.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No review rows match the current filter.
        </Typography>
      ) : null}

      <Stack spacing={1.25}>
        {visibleGroups.map((change, groupIndex) => (
          <Paper key={change.change_id || change.id || groupIndex} variant="outlined" sx={{ p: 1.25, backgroundColor: "background.paper" }}>
            {(() => {
              const verificationChip = getVerificationChip(change.verification_status);
              return (
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ justifyContent: "space-between", mb: 1 }}>
              <Box>
                <Typography variant="subtitle2">
                  #{change.change_id || change.id} - {change.title || "Untitled linked change"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Order {change.order ?? change.full_change_order ?? groupIndex + 1} - Status {change.status || "unknown"}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap" }}>
                <Chip size="small" label={verificationChip.label} color={verificationChip.color} variant={verificationChip.variant} />
                <Chip size="small" label={`Code ${change.summary?.code_file_count ?? 0}`} variant="outlined" />
                <Chip size="small" label={`Docs ${change.summary?.documentation_row_count ?? 0}`} variant="outlined" />
                <Chip size="small" label={`Diffs ${change.summary?.diff_available_count ?? 0}`} color="success" variant="outlined" />
                <Chip size="small" label={`No diff ${(change.summary?.diff_missing_count ?? 0) + (change.summary?.history_only_count ?? 0)}`} variant="outlined" />
              </Stack>
            </Stack>
              );
            })()}

            {change.verification_status?.steps?.length ? (
              <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", rowGap: 0.75, mb: 1 }}>
                {change.verification_status.steps.map((step) => (
                  <Chip
                    key={step.key}
                    size="small"
                    label={`${step.label}: ${step.status_label || "Not run"}`}
                    color={getStepChipColor(step.state)}
                    variant={getStepChipColor(step.state) === "default" ? "outlined" : "filled"}
                    title={step.message || step.timestamp || ""}
                  />
                ))}
              </Stack>
            ) : null}

            {(() => {
              const changeId = change.change_id || change.id;
              const draft = humanReviewDrafts[changeId] || {
                status: change.human_review?.status || "not_started",
                notes: change.human_review?.notes || "",
              };
              return (
                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: "rgba(25,118,210,0.03)" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                    Human review status is separate from automated verification and smoke test results.
                  </Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ alignItems: { xs: "stretch", md: "flex-start" } }}>
                    <TextField
                      size="small"
                      select
                      label="Human review"
                      value={draft.status}
                      onChange={(event) => updateHumanReviewDraft(changeId, { status: event.target.value })}
                      sx={{ minWidth: { xs: "100%", md: 190 } }}
                    >
                      {HUMAN_REVIEW_STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Review notes"
                      value={draft.notes}
                      onChange={(event) => updateHumanReviewDraft(changeId, { notes: event.target.value })}
                      fullWidth
                      multiline
                      minRows={1}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => saveHumanReview(change)}
                      disabled={humanReviewSavingId === changeId}
                      sx={{ minWidth: 110 }}
                    >
                      {humanReviewSavingId === changeId ? "Saving..." : "Save Review"}
                    </Button>
                  </Stack>
                  {change.human_review?.updated_at ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      Last saved {change.human_review.updated_at}
                    </Typography>
                  ) : null}
                </Paper>
              );
            })()}

            {change.reviewRows.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No changed file rows were recorded for this linked change.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {change.reviewRows.map((row, rowIndex) => {
                  const stateChip = getStateChip(row.change_indicator);
                  const isDocumentation = getRowKind(row) === "documentation";
                  const diffChip = getDiffStatusChip(row);
                  return (
                    <Box
                      key={getRowKey(row, rowIndex)}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "minmax(240px, 1.2fr) 130px 120px 130px minmax(220px, 1fr) 120px" },
                        gap: 1,
                        alignItems: "center",
                        border: isDocumentation ? "1px solid rgba(156,39,176,0.35)" : "1px solid rgba(0,0,0,0.08)",
                        borderRadius: 1,
                        backgroundColor: isDocumentation ? "rgba(156,39,176,0.04)" : "background.paper",
                        p: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                          {getRowPath(row)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.saved_at || "No saved timestamp"}
                        </Typography>
                      </Box>
                      <Chip size="small" label={isDocumentation ? "Documentation" : row.kind || "Code"} color={isDocumentation ? "secondary" : "default"} variant="outlined" />
                      <Chip size="small" label={stateChip.label} color={stateChip.color} variant={stateChip.color === "default" ? "outlined" : "filled"} />
                      <Chip size="small" label={diffChip.label} color={diffChip.color} variant={diffChip.variant} />
                      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                        {row.save_summary || "No save summary"}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!row.diff_available && !row.preview_log_id && !row.diff_text}
                        onClick={() => openDiff(row)}
                      >
                        Preview Diff
                      </Button>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        ))}
      </Stack>

      <Dialog open={diffDialogOpen} onClose={() => setDiffDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Review diff</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1, fontFamily: "monospace", overflowWrap: "anywhere" }}>
            {diffTitle}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {diffLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}
          {diffError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {diffError}
            </Alert>
          ) : null}
          {!diffLoading && !diffError ? (
            <RichDiffViewer
              diffText={diffText}
              emptyMessage="No diff text stored."
              maxHeight={520}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FullChangeReviewWorkspace;
